import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { agendaVoiceSttApi, checkApiReachable } from '../services/agendaVoiceSttApi'

const DEFAULT_SAMPLE_RATE = 16000
const BROWSER_UNSUPPORTED_MESSAGE =
  'Voice input is unavailable in this browser. Use Chrome/Edge or continue typing.'
const MIC_DENIED_MESSAGE =
  'Mic access was blocked. Click the lock icon in your browser\'s address bar to allow microphone access, then try again.'
const STT_UNAVAILABLE_MESSAGE = 'Voice transcription is currently unavailable. You can keep typing.'

function encodePcm16(float32Array) {
  const buffer = new ArrayBuffer(float32Array.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < float32Array.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]))
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
  }
  return buffer
}

function appendTranscriptText(current, addition) {
  const next = String(addition ?? '').trim()
  if (!next) return current
  if (!current) return next
  return /\s$/.test(current) ? `${current}${next}` : `${current} ${next}`
}

function createSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `setup-${crypto.randomUUID()}`
  }
  return `setup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function hasVoiceBrowserSupport() {
  if (!navigator?.mediaDevices?.getUserMedia) return false
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  if (!AudioContextCtor) return false
  return typeof AudioContextCtor.prototype.createScriptProcessor === 'function'
}

export function useSetupVoiceInput({ value, onChange, disabled }) {
  const [voiceStatus, setVoiceStatus] = useState('idle')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [voiceError, setVoiceError] = useState('')

  const sttRef = useRef({
    ws: null,
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    gain: null,
    closing: false,
  })
  const startValueRef = useRef('')
  const valueRef = useRef(value)
  valueRef.current = value
  const sessionIdRef = useRef('')
  const mountedRef = useRef(true)
  const idleTimerRef = useRef(null)
  const recordingTimeoutRef = useRef(null)

  const teardownAudio = useCallback(() => {
    const sttState = sttRef.current
    if (sttState.processor) {
      sttState.processor.disconnect()
    }
    if (sttState.source) {
      sttState.source.disconnect()
    }
    if (sttState.gain) {
      sttState.gain.disconnect()
    }
    if (sttState.stream) {
      sttState.stream.getTracks().forEach((track) => track.stop())
    }
    if (sttState.audioContext) {
      sttState.audioContext.close().catch(() => {})
    }
    sttState.stream = null
    sttState.audioContext = null
    sttState.processor = null
    sttState.source = null
    sttState.gain = null
  }, [])

  const closeSocket = useCallback(() => {
    const sttState = sttRef.current
    if (sttState.ws && sttState.ws.readyState <= WebSocket.OPEN) {
      try {
        sttState.ws.close()
      } catch {
        // ignore close failures
      }
    }
    sttState.ws = null
  }, [])

  const stopVoice = useCallback(
    (cancel = false, { preserveErrorState = false } = {}) => {
      if (!mountedRef.current) return
      const sttState = sttRef.current
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current)
        recordingTimeoutRef.current = null
      }
      sttState.closing = true
      if (!preserveErrorState) {
        setVoiceStatus('transcribing')
      }
      setInterimTranscript('')

      if (cancel) {
        onChange(startValueRef.current)
      }

      if (sttState.ws && sttState.ws.readyState === WebSocket.OPEN) {
        sttState.ws.send(JSON.stringify({ type: 'stop' }))
      }
      teardownAudio()
      closeSocket()
      if (!preserveErrorState) {
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current)
        }
        idleTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return
          setVoiceStatus('idle')
        }, 500)
      }
    },
    [closeSocket, onChange, teardownAudio]
  )

  const applyFinalTranscript = useCallback(
    (text) => {
      onChange(appendTranscriptText(valueRef.current, text))
    },
    [onChange]
  )

  const startVoice = useCallback(async () => {
    if (disabled) return
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
    }
    if (!hasVoiceBrowserSupport()) {
      setVoiceStatus('error')
      setVoiceError(BROWSER_UNSUPPORTED_MESSAGE)
      return
    }

    setVoiceError('')
    setInterimTranscript('')
    setVoiceStatus('requesting_microphone')

    const apiError = await checkApiReachable()
    if (apiError) {
      setVoiceStatus('error')
      setVoiceError(`${STT_UNAVAILABLE_MESSAGE} (${apiError})`)
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext
      if (!AudioContextCtor) {
        setVoiceStatus('error')
        setVoiceError(BROWSER_UNSUPPORTED_MESSAGE)
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      startValueRef.current = value

      const audioContext = new AudioContextCtor({ sampleRate: DEFAULT_SAMPLE_RATE })
      await audioContext.resume()
      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      const gain = audioContext.createGain()
      gain.gain.value = 0

      source.connect(processor)
      processor.connect(gain)
      gain.connect(audioContext.destination)

      const sessionId = createSessionId()
      sessionIdRef.current = sessionId
      const ws = agendaVoiceSttApi.createSocket(sessionId)
      sttRef.current.ws = ws
      sttRef.current.stream = stream
      sttRef.current.audioContext = audioContext
      sttRef.current.processor = processor
      sttRef.current.source = source
      sttRef.current.gain = gain
      sttRef.current.closing = false

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'start',
            sessionId: sessionIdRef.current,
            sampleRate: audioContext.sampleRate || DEFAULT_SAMPLE_RATE,
            encoding: 'pcm_s16le',
            languageCode: 'en',
          })
        )
      }

      ws.onmessage = (event) => {
        if (!mountedRef.current) return
        try {
          const payload = JSON.parse(String(event.data))
          if (payload.type === 'ready') {
            setVoiceStatus('listening')
            recordingTimeoutRef.current = setTimeout(() => {
              if (!mountedRef.current) return
              stopVoice(false)
            }, 60000)
            return
          }
          if (payload.type === 'partial') {
            setInterimTranscript(payload.text || '')
            return
          }
          if (payload.type === 'final') {
            setInterimTranscript('')
            applyFinalTranscript(payload.text || '')
            return
          }
          if (payload.type === 'error') {
            setVoiceStatus('error')
            setVoiceError(payload.message || STT_UNAVAILABLE_MESSAGE)
            stopVoice(false, { preserveErrorState: true })
          }
        } catch {
          // ignore malformed frames
        }
      }

      ws.onerror = () => {
        if (!mountedRef.current) return
        setVoiceStatus('error')
        setVoiceError(STT_UNAVAILABLE_MESSAGE)
        stopVoice(false, { preserveErrorState: true })
      }

      ws.onclose = () => {
        if (!mountedRef.current) return
        if (sttRef.current.closing) {
          sttRef.current.closing = false
          return
        }
        setVoiceStatus('error')
        setVoiceError(STT_UNAVAILABLE_MESSAGE)
      }

      processor.onaudioprocess = (audioEvent) => {
        const sttState = sttRef.current
        if (!sttState.ws || sttState.ws.readyState !== WebSocket.OPEN) return
        const inputData = audioEvent.inputBuffer.getChannelData(0)
        sttState.ws.send(encodePcm16(inputData))
      }
    } catch {
      setVoiceStatus('error')
      setVoiceError(MIC_DENIED_MESSAGE)
    }
  }, [applyFinalTranscript, disabled, stopVoice, value])

  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current)
      }
      teardownAudio()
      closeSocket()
    }
  }, [closeSocket, teardownAudio])

  const statusLabel = useMemo(() => {
    if (voiceStatus === 'requesting_microphone') return 'Waiting for mic…'
    if (voiceStatus === 'listening') return 'Listening…'
    if (voiceStatus === 'transcribing') return 'Processing…'
    if (voiceStatus === 'error') return 'Voice error'
    return ''
  }, [voiceStatus])

  return {
    voiceStatus,
    statusLabel,
    interimTranscript,
    voiceError,
    isListening: voiceStatus === 'listening' || voiceStatus === 'requesting_microphone',
    startVoice,
    stopVoice,
    cancelVoice: () => stopVoice(true),
  }
}

